import * as React from 'react';
import {
  ArrowCounterClockwise,
  ArrowSquareOut,
  Copy,
  Wrench,
  XCircle,
} from '@phosphor-icons/react';
import { useQuery } from 'convex/react';
import { Button, Drawer } from '@fragments-sdk/ui';
import { api } from '@/convex';
import { formatRuleLabel } from '@/lib/govern-rule-catalog';
import {
  formatFindingTimestamp,
  findingTrustSummary,
  parseFindingEvidence,
  parseFindingFix,
  stringifyJsonPreview,
} from './finding-detail-model';
import type { FindingDoc, FindingTimeline } from './findings-types';
import {
  buildGitHubUrl,
  canonicalSeverity,
  extractRepoFromBinding,
  findingLifecycleLabel,
  findingSourceSummary,
  formatCategoryLabel,
  isFixable,
  parseFindingAttributes,
  resolveSuggestion,
  severityLabel,
  stripCategoryPrefix,
} from './findings-display-model';
import s from './findings.module.scss';

export function FindingDetailPanel({
  finding,
  projectRepoFullName,
  onClose,
  onCopy,
  onFix,
  onIgnore,
  onReopen,
  canCreateWaiver,
  fixLoading,
}: {
  finding: FindingDoc;
  projectRepoFullName?: string;
  onClose: () => void;
  onCopy: (text: string) => void;
  onFix: (finding: FindingDoc) => void;
  onIgnore: (finding: FindingDoc) => void;
  onReopen: (finding: FindingDoc) => void;
  canCreateWaiver: boolean;
  fixLoading: boolean;
}) {
  const attributes = parseFindingAttributes(finding);
  const evidence = parseFindingEvidence(finding.evidenceJson);
  const fix = parseFindingFix(finding.fixJson);
  const trust = findingTrustSummary({
    evidenceJson: finding.evidenceJson,
    fixJson: finding.fixJson,
    source: finding.source,
    filePath: finding.filePath,
    commitSha: finding.commitSha,
    sourceVerified: finding.sourceVerified,
    sourceVerificationMethod: finding.sourceVerificationMethod,
  });
  const suggestion = resolveSuggestion(finding);
  const timeline = useQuery(api.findingsEvents.getFindingTimeline, {
    findingId: finding._id as any,
    order: 'desc',
    limit: 8,
  }) as FindingTimeline | undefined;
  const repository = extractRepoFromBinding(finding) ?? projectRepoFullName;
  const sourceUrl = finding.filePath
    ? buildGitHubUrl(
        repository,
        finding.commitSha,
        finding.filePath,
        finding.line,
      )
    : null;
  const lifecycleLabel = findingLifecycleLabel(finding.status);
  const sourceLabel = findingSourceSummary(finding.source, finding.prNumber);
  const fixLabel =
    fix?.title ??
    fix?.kind ??
    suggestion?.display ??
    'No structured fix was stored for this finding.';
  const patchPreview = fix?.patch ? stringifyJsonPreview(fix.patch) : null;
  const attributesPreview =
    Object.keys(attributes).length > 0
      ? stringifyJsonPreview(attributes)
      : null;

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Drawer.Content side="right" size="xl" className={s.detailPanel}>
        <Drawer.Header className={s.detailHeader}>
          <div>
            <div className={s.detailKicker}>
              <span data-status={finding.status}>{lifecycleLabel}</span>
              <span>{sourceLabel}</span>
            </div>
            <Drawer.Title className={s.detailTitle}>
              {stripCategoryPrefix(finding.message)}
            </Drawer.Title>
          </div>
          <Drawer.Close className={s.detailClose} />
        </Drawer.Header>

        <div className={s.detailBadges}>
          <span
            className={s.severityBadge}
            data-severity={canonicalSeverity(finding.severity)}
          >
            {severityLabel(finding.severity)}
          </span>
          <span>{formatRuleLabel(finding.ruleId)}</span>
          {finding.code && <span>{finding.code}</span>}
          {finding.ruleVersion && <span>v{finding.ruleVersion}</span>}
          <span>{trust.label}</span>
        </div>

        <Drawer.Body className={s.detailBody}>
          <section className={s.detailSection}>
            <h3>Location</h3>
            <div className={s.detailGrid}>
              <DetailItem label="Path">
                {finding.filePath ? (
                  <span className={s.detailCode}>{finding.filePath}</span>
                ) : (
                  'Not recorded'
                )}
              </DetailItem>
              <DetailItem label="Line">
                {finding.line != null
                  ? `${finding.line}${finding.column != null ? `:${finding.column}` : ''}`
                  : 'Not recorded'}
              </DetailItem>
              <DetailItem label="Branch">
                {finding.branch ?? 'Not recorded'}
              </DetailItem>
              <DetailItem label="Commit">
                {finding.commitSha
                  ? finding.commitSha.slice(0, 12)
                  : 'Not recorded'}
              </DetailItem>
              <DetailItem label="First seen">
                {formatFindingTimestamp(finding.firstDetectedAt)}
              </DetailItem>
              <DetailItem label="Last seen">
                {formatFindingTimestamp(finding.lastSeenAt)}
              </DetailItem>
              <DetailItem label="Evidence trust">{trust.detail}</DetailItem>
            </div>
            <div className={s.detailInlineActions}>
              {sourceUrl && (
                <Button
                  as="a"
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                  size="xs"
                  className={s.detailInlineButton}
                >
                  <ArrowSquareOut size={12} />
                  Open source
                </Button>
              )}
              {finding.filePath && (
                <Button
                  type="button"
                  variant="secondary"
                  size="xs"
                  className={s.detailInlineButton}
                  onClick={() => onCopy(finding.filePath!)}
                >
                  <Copy size={12} />
                  Copy path
                </Button>
              )}
            </div>
          </section>

          <section className={s.detailSection}>
            <h3>Fix context</h3>
            <p className={s.detailText}>{fixLabel}</p>
            {fix && (
              <div className={s.detailGrid}>
                <DetailItem label="Kind">
                  {fix.kind ?? 'Not recorded'}
                </DetailItem>
                <DetailItem label="Safety">
                  {fix.deterministic === true ? 'Deterministic' : 'Guided'}
                </DetailItem>
                <DetailItem label="Intent">
                  {finding.intent ?? 'Not recorded'}
                </DetailItem>
                <DetailItem label="Suggested from">
                  {finding.suggestedFrom ?? 'Not recorded'}
                </DetailItem>
              </div>
            )}
            {patchPreview && <pre className={s.detailPre}>{patchPreview}</pre>}
          </section>

          <section className={s.detailSection}>
            <h3>Evidence</h3>
            {evidence.length > 0 ? (
              <div className={s.evidenceList}>
                {evidence.map((item, index) => (
                  <div
                    key={`${item.factId ?? item.label ?? 'evidence'}-${index}`}
                    className={s.evidenceItem}
                  >
                    <strong>
                      {item.label ?? item.factId ?? `Evidence ${index + 1}`}
                    </strong>
                    {(item.filePath || item.line != null) && (
                      <span>
                        {item.filePath ?? 'Unknown file'}
                        {item.line != null ? `:${item.line}` : ''}
                        {item.column != null ? `:${item.column}` : ''}
                      </span>
                    )}
                    {item.excerpt && <code>{item.excerpt}</code>}
                  </div>
                ))}
              </div>
            ) : (
              <p className={s.detailMuted}>
                No structured evidence was stored for this finding.
              </p>
            )}
          </section>

          <section className={s.detailSection}>
            <h3>Timeline</h3>
            {timeline === undefined ? (
              <p className={s.detailMuted}>Loading timeline...</p>
            ) : timeline.items.length > 0 ? (
              <div className={s.timelineList}>
                {timeline.items.map((item) => {
                  const meta = timelineMetaParts(item);
                  const contextUrl = timelineContextUrl(item, finding);
                  return (
                    <div key={item.id} className={s.timelineItem}>
                      <span data-kind={item.kind} />
                      <div>
                        <strong>{timelineKindLabel(item.kind)}</strong>
                        <p>{formatFindingTimestamp(item.createdAt)}</p>
                        {meta.length > 0 && <small>{meta.join(' · ')}</small>}
                        {contextUrl && (
                          <a
                            href={contextUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.timelineLink}
                          >
                            Open context
                            <ArrowSquareOut size={10} />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={s.detailMuted}>
                No historical events were stored for this finding.
              </p>
            )}
          </section>

          <section className={s.detailSection}>
            <h3>Identity</h3>
            <div className={s.detailGrid}>
              <DetailItem label="Lifecycle">
                {findingLifecycleLabel(finding.status)}
              </DetailItem>
              <DetailItem label="Severity">
                {severityLabel(finding.severity)}
              </DetailItem>
              <DetailItem label="Rule id">
                <span className={s.detailCode}>{finding.ruleId}</span>
              </DetailItem>
              <DetailItem label="Fingerprint">
                <span className={s.detailCode}>{finding.fingerprint}</span>
              </DetailItem>
              <DetailItem label="Category">
                {finding.category
                  ? formatCategoryLabel(finding.category)
                  : 'Not recorded'}
              </DetailItem>
              <DetailItem label="Source">
                {finding.source ?? 'repo-scan'}
              </DetailItem>
            </div>
            {attributesPreview && (
              <pre className={s.detailPre}>{attributesPreview}</pre>
            )}
          </section>
        </Drawer.Body>

        <Drawer.Footer className={s.detailFooter}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={s.detailFooterButton}
            onClick={() => onCopy(finding.fingerprint)}
          >
            <Copy size={12} />
            Copy fingerprint
          </Button>
          {finding.status === 'open' ? (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className={s.detailFooterButton}
                disabled={!canCreateWaiver}
                onClick={() => onIgnore(finding)}
              >
                <XCircle size={12} />
                {canCreateWaiver ? 'Ignore' : 'Upgrade for waivers'}
              </Button>
              {isFixable(finding) && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className={s.detailFooterButton}
                  disabled={fixLoading}
                  onClick={() => onFix(finding)}
                >
                  <Wrench size={12} />
                  Fix
                </Button>
              )}
            </>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="sm"
              className={s.detailFooterButton}
              onClick={() => onReopen(finding)}
            >
              <ArrowCounterClockwise size={12} />
              Reopen
            </Button>
          )}
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}

export function DetailItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={s.detailItem}>
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

function timelineKindLabel(kind: string): string {
  if (kind === 'produced') return 'Became current';
  if (kind === 'updated') return 'Still current in scan';
  if (kind === 'resolved') return 'Moved to history';
  if (kind === 'ignored') return 'Ignored into history';
  if (kind === 'fixed') return 'Fix created';
  if (kind === 'commented') return 'Commented';
  if (kind === 'reopened') return 'Returned to current';
  if (kind === 'dismissed') return 'Dismissed into history';
  if (kind === 'shown') return 'Shown to operator';
  return kind.replace(/_/g, ' ');
}

function timelineMetaParts(item: FindingTimeline['items'][number]): string[] {
  const parts = [
    `Source: ${findingSourceSummary(item.repository.source, item.repository.prNumber)}`,
  ];
  if (item.repository.branch) parts.push(`Branch: ${item.repository.branch}`);
  if (item.repository.commitSha) {
    parts.push(`Commit: ${item.repository.commitSha.slice(0, 12)}`);
  }
  if (item.actor && item.actor !== item.repository.source) {
    parts.push(`Actor: ${item.actor}`);
  }
  if (item.run && item.kind === 'resolved') {
    parts.push(`${item.run.occurrenceCount} prior occurrences`);
  }
  return parts;
}

function timelineContextUrl(
  item: FindingTimeline['items'][number],
  finding: FindingDoc,
): string | null {
  const repo = item.repository.repoFullName ?? extractRepoFromBinding(finding);
  if (!repo) return null;
  if (item.repository.commitSha && finding.filePath) {
    return buildGitHubUrl(
      repo,
      item.repository.commitSha,
      finding.filePath,
      finding.line,
    );
  }
  if (item.repository.prNumber) {
    return `https://github.com/${repo}/pull/${item.repository.prNumber}`;
  }
  if (item.repository.commitSha) {
    return `https://github.com/${repo}/commit/${item.repository.commitSha}`;
  }
  return null;
}
